import React, { useState, useEffect } from 'react'
import { Search, Loader2 } from 'lucide-react'
import { useTranslation } from '../../hooks/useTranslation'
import arTranslations from '../../lang/ar.json'
import enTranslations from '../../lang/en.json'
import { listProducts } from '../../services/catalog'
import type { Product as ApiProduct } from '../../types/product'

interface SearchSuggestionsProps {
  query: string
}

const SearchSuggestions: React.FC<SearchSuggestionsProps> = ({ query }) => {
  const { t, isRTL } = useTranslation()
  const [products, setProducts] = useState<ApiProduct[]>([])
  const [loading, setLoading] = useState(false)

  // بيانات اقتراحات البحث المحدثة لمشروع BadanIbex® (تطبيقات، أنظمة، وقوالب)
  const staticSuggestions = [

    // English suggestions

    'Laravel CMS',

    'WordPress Plugins',

    'React Components',

    'PHP Scripts',

    'JavaScript Libraries',

    'CSS Frameworks',

    'Mobile Apps',

    'E-commerce Solutions',

    // Arabic suggestions

    'قالب Laravel CMS',

    'إضافات WordPress',

    'مكونات React',

    'سكريبتات PHP',

    'مكتبات JavaScript',

    'أطر عمل CSS',

    'تطبيقات الموبايل',

    'حلول التجارة الإلكترونية'

  ]

  // Load products for search
  useEffect(() => {
    const loadProducts = async () => {
      if (query.length > 1) {
        setLoading(true)
        try {
          const response = await listProducts(query, 10)
          setProducts(response.data)
        } catch (error) {
          console.error('Error loading products for search:', error)
        } finally {
          setLoading(false)
        }
      }
    }

    loadProducts()
  }, [query])

  // Build translation-based product suggestions so Arabic/English names map to slugs
  const translationProducts = (() => {
    const dicts = [arTranslations as any, enTranslations as any]
    const collected: { slug: string; name: string }[] = []
    for (const d of dicts) {
      const products = d?.products || {}
      for (const [slug, info] of Object.entries(products)) {
        const name = (info as any)?.name as string | undefined
        if (name) collected.push({ slug, name })
      }
    }
    return collected
  })()

  // Filter static suggestions
  const filteredStaticSuggestions = staticSuggestions.filter(suggestion =>
    suggestion.toLowerCase().includes(query.toLowerCase()) ||
    suggestion.includes(query)
  )

  // Filter products from API
  const filteredProducts = products
    .map(p => ({
      ...p, score: (
        (p.name?.toLowerCase().includes(query.toLowerCase()) ? 3 : 0) +
        (p.description?.toLowerCase().includes(query.toLowerCase()) ? 2 : 0) +
        (p.slug?.toLowerCase().includes(query.toLowerCase()) ? 1 : 0)
      )
    }))
    .filter(p => p.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, 5)

  // Filter translation-based products
  const filteredTranslated = translationProducts
    .filter(tp => tp.name.toLowerCase().includes(query.toLowerCase()) || tp.name.includes(query))
    .slice(0, 5)

  // Combine suggestions (products first, then static)
  const allSuggestions = [
    ...filteredProducts.map(product => ({
      type: 'product' as const,
      text: product.name,
      product
    })),
    ...filteredTranslated.map(tp => ({
      type: 'translation' as const,
      text: tp.name,
      product: { slug: tp.slug } as any
    })),
    ...filteredStaticSuggestions.map(suggestion => ({
      type: 'static' as const,
      text: suggestion,
      product: null
    }))
  ].slice(0, 5)

  return (
    <div className="bg-surface-2 border border-border rounded-lg shadow-2xl overflow-hidden transition-all duration-200">
      <div className="py-2">
        {loading ? (
          <div className="flex items-center justify-center gap-2 px-4 py-3 text-sm text-text-2">
            <Loader2 size={16} className="animate-spin text-gold" />
            <span>{t('search.suggestions.loading')}</span>
          </div>
        ) : allSuggestions.length > 0 ? (
          allSuggestions.map((suggestion, index) => (
            <button
              key={index}
              aria-label={`${suggestion.type === 'product' ? (t('search.suggestions.product') as string) : (isRTL ? 'اقتراح بحث' : 'Search suggestion')}: ${suggestion.text}`}
              className={`w-full flex items-center gap-3 px-4 py-2.5 text-sm text-[var(--text)] hover:bg-[var(--surface)] transition-colors duration-150 ${isRTL ? 'text-right' : 'text-left'
                }`}
              onClick={() => {
                const term = suggestion.type === 'product' && suggestion.product
                  ? suggestion.product.slug
                  : suggestion.text
                const q = encodeURIComponent(term)
                window.location.href = `/catalog?q=${q}`
              }}
            >
              <Search size={15} className="text-[var(--text)] opacity-70 flex-shrink-0" />
              <span className="truncate flex-1 font-medium">{suggestion.text}</span>
              {suggestion.type === 'product' && (
                <span className="text-[10px] font-bold text-amber-700 dark:text-[var(--gold)] bg-amber-500/10 dark:bg-[var(--gold)]/10 px-2 py-0.5 rounded border border-amber-500/20 dark:border-[var(--gold)]/20 flex-shrink-0">
                  {t('search.suggestions.product')}
                </span>
              )}
            </button>
          ))
        ) : (
          <div className="px-4 py-3 text-sm text-text-muted text-center">
            {t('search.suggestions.noResults')}
          </div>
        )}
      </div>
    </div>
  )
}

export default SearchSuggestions